import React from "react";
import { Card } from "antd";
import PropTypes from 'prop-types';
import styles from './PagesWrapper.module.css';
import PagesHeader from "../PagesHeader";
import { AccessBanner } from "../components/Ownership";

/**
 * A wrapper component for pages that provides consistent layout and styling
 * Supports optional ownership access banner for content-creator pages
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - The content to be wrapped
 * @param {boolean} [props.showHeader=true] - Whether to show the page header
 * @param {string} [props.className] - Additional CSS class name
 * @param {Object} [props.ownership] - Ownership info for access banner (optional)
 * @param {string} [props.ownership.accessLevel] - User's access level
 * @param {Object} [props.ownership.ownerInfo] - Main owner information
 * @param {boolean} [props.ownership.canEdit] - Whether user can edit
 * @param {boolean} [props.ownership.canDelete] - Whether user can delete
 * @returns {JSX.Element} The wrapped content
 */
export function PagesWrapper({ 
  children, 
  showHeader = true,
  className = '',
  ownership = null,
}) {
  const cardClassName = `${showHeader ? styles['pages-wrapper-card'] : styles['pages-wrapper-card-full-screen']} ${className}`.trim();

  // Determine if we should show the access banner
  const showAccessBanner = ownership && 
    ownership.accessLevel && 
    ownership.accessLevel.toLowerCase() !== 'mainowner';

  return (
    <div className={styles['pages-wrapper']}>
      {showHeader && <PagesHeader />}
      {showAccessBanner && (
        <AccessBanner
          accessLevel={ownership.accessLevel}
          ownerInfo={ownership.ownerInfo}
          canEdit={ownership.canEdit}
          canDelete={ownership.canDelete}
        />
      )}
      <Card 
        className={cardClassName}
      >
        {children}
      </Card>
    </div>
  );
}

PagesWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  showHeader: PropTypes.bool,
  className: PropTypes.string,
  ownership: PropTypes.shape({
    accessLevel: PropTypes.string,
    ownerInfo: PropTypes.object,
    canEdit: PropTypes.bool,
    canDelete: PropTypes.bool,
  }),
};

export default PagesWrapper;